/**
 * Setup Layers — a port of the original's layer dialog (OGdlg07.cpp).
 *
 * Columns are the original's: number, name, type, top/bottom, screen colour,
 * print colour. Operations are Insert, Delete, Rename and reorder.
 *
 * Every edit goes through the core layer functions, which do the table change
 * and the entity remap together. Layer number *is* position in this format, so
 * moving a layer renumbers others; doing the two separately would silently
 * turn copper into silkscreen.
 */

import {
  type CadDocument, LAYER_TYPE_NAMES, LayerType,
} from '@sircad/core/model.ts';
import {
  countEntitiesOn, deleteLayer, insertLayer, moveLayer, updateLayer,
} from '@sircad/core/geom/layers.ts';
import { PAD_MASTER_LAYER } from '@sircad/core/geom/board.ts';
import { button, buttons, errorLine, openDialog } from './dialog.ts';

export interface LayerDialogOptions {
  doc: CadDocument;
  /** Called after any change so the shell can redraw and mark the doc dirty. */
  onChange(label: string): void;
}

const TYPE_ORDER: LayerType[] = [
  LayerType.Copper, LayerType.Plane, LayerType.Silk, LayerType.Mask,
  LayerType.Paste, LayerType.Symbols, LayerType.Schematic, LayerType.Drawing,
];

export function showLayerDialog(opts: LayerDialogOptions): void {
  let selected = 1;
  /** The row elements, layer by layer, so selecting one need not rebuild them. */
  let rows: HTMLElement[] = [];

  openDialog({
    title: 'Setup Layers',
    build(body, handle) {
      body.classList.add('layer-dialog');
      const err = errorLine(body);

      const table = document.createElement('div');
      table.className = 'layer-table';
      body.append(table);

      const row = buttons(body);
      button(row, 'Insert', () => {
        try {
          selected = insertLayer(opts.doc, Math.max(1, selected));
          changed('Insert layer');
        } catch (e) { fail(e as Error); }
      });

      button(row, 'Delete', () => {
        const layer = opts.doc.layers[selected];
        if (!layer) return;
        try {
          const used = countEntitiesOn(opts.doc, selected);
          if (used > 0) {
            // Losing geometry silently would be unforgivable, so say what is
            // on the layer and offer to keep it.
            const keep = window.confirm(
              `"${layer.name}" has ${used} object${used === 1 ? '' : 's'} on it.\n\n`
              + 'OK to move them to the layer above.\n'
              + 'Cancel to delete them along with the layer.',
            );
            deleteLayer(opts.doc, selected, keep ? { moveTo: Math.max(0, selected - 1) } : {});
          } else {
            deleteLayer(opts.doc, selected, {});
          }
          selected = Math.min(selected, opts.doc.layers.length - 1);
          changed('Delete layer');
        } catch (e) { fail(e as Error); }
      });

      button(row, 'Move up', () => {
        try { moveLayer(opts.doc, selected, selected - 1); selected--; changed('Move layer'); }
        catch (e) { fail(e as Error); }
      });

      button(row, 'Move down', () => {
        try { moveLayer(opts.doc, selected, selected + 1); selected++; changed('Move layer'); }
        catch (e) { fail(e as Error); }
      });

      button(row, 'Close', () => handle.close(), true);

      const note = document.createElement('p');
      note.className = 'modal-hint';
      note.textContent =
        'Layer 0 is the pad master: through-hole pads on it reach every copper '
        + 'layer. It cannot be moved or deleted.';
      body.append(note);

      function fail(e: Error): void {
        err.textContent = e.message;
        err.hidden = false;
      }

      function changed(label: string): void {
        err.hidden = true;
        opts.onChange(label);
        render();
      }

      /**
       * Highlight a row, without rebuilding the table.
       *
       * Re-rendering here used to be how selection worked, and it took the
       * clicked row's own name box down with it: the field was focused and
       * then replaced before a caret could appear, so the layer names could
       * only be reached by tabbing. Nothing but the highlight depends on which
       * row is chosen, so nothing but the highlight needs to change.
       *
       * On mousedown rather than click. Editing a name and then clicking
       * another row blurs the field, which commits the rename and does rebuild
       * the table — and a click whose press and release land on two different
       * elements is not a click at all, so the selection was lost.
       */
      function choose(index: number): void {
        selected = index;
        rows.forEach((el, i) => el.classList.toggle('sel', i === selected));
      }

      function render(): void {
        table.replaceChildren();
        rows = [];

        const head = document.createElement('div');
        head.className = 'layer-row head';
        for (const h of ['#', 'Name', 'Type', 'Side', 'Screen', 'Print', 'Used']) {
          const c = document.createElement('span');
          c.textContent = h;
          head.append(c);
        }
        table.append(head);

        opts.doc.layers.forEach((layer, index) => {
          const r = document.createElement('div');
          r.className = 'layer-row'
            + (index === selected ? ' sel' : '')
            + (index === PAD_MASTER_LAYER ? ' master' : '');
          r.addEventListener('mousedown', () => choose(index));
          rows.push(r);

          const num = document.createElement('span');
          num.className = 'lnum';
          num.textContent = String(index);

          const name = document.createElement('input');
          name.className = 'lname';
          name.value = layer.name;
          name.addEventListener('change', () => {
            updateLayer(opts.doc, index, { name: name.value.trim() || `Layer ${index}` });
            changed('Rename layer');
          });

          const type = document.createElement('select');
          type.className = 'ltype';
          for (const t of TYPE_ORDER) {
            const o = document.createElement('option');
            o.value = String(t);
            o.textContent = LAYER_TYPE_NAMES[t];
            type.append(o);
          }
          type.value = String(layer.type);
          type.addEventListener('change', () => {
            updateLayer(opts.doc, index, { type: Number(type.value) as LayerType });
            changed('Change layer type');
          });

          // Three, not two. A CIRCAD file has one bit here and can only say
          // top or bottom, so every buried layer in every existing design
          // arrives claiming to be on top. That is invisible while the editor
          // draws each layer in its own colour, and wrong the moment anything
          // shows the board as it would look — where inner copper must not
          // appear on either face. Inner still plots its own film.
          const side = document.createElement('select');
          side.className = 'lside';
          for (const [v, label] of [
            ['0', 'Top'], ['i', 'Inner'], ['1', 'Bottom'],
          ] as const) {
            const o = document.createElement('option');
            o.value = v;
            o.textContent = label;
            side.append(o);
          }
          side.value = layer.inner ? 'i' : layer.bottom ? '1' : '0';
          side.addEventListener('change', () => {
            const v = side.value;
            updateLayer(opts.doc, index, {
              inner: v === 'i',
              // An inner layer keeps whichever face it was nearer, because
              // that is all a CIRCAD file will remember of it.
              bottom: v === 'i' ? layer.bottom : v === '1',
            });
            changed('Change layer side');
          });

          const screen = colourInput(layer.screen, (v) => {
            updateLayer(opts.doc, index, { screen: v });
            changed('Change layer colour');
          });
          const print = colourInput(layer.print, (v) => {
            updateLayer(opts.doc, index, { print: v });
            changed('Change print colour');
          });

          const used = document.createElement('span');
          used.className = 'lused';
          const n = countEntitiesOn(opts.doc, index);
          used.textContent = n === 0 ? '—' : String(n);

          r.append(num, name, type, side, screen, print, used);
          table.append(r);
        });
      }

      render();
    },
  });
}

function colourInput(value: number, onChange: (v: number) => void): HTMLInputElement {
  const input = document.createElement('input');
  input.type = 'color';
  input.className = 'lcolour';
  input.value = `#${(value >>> 0).toString(16).padStart(6, '0').slice(-6)}`;
  input.addEventListener('change', () => {
    onChange(parseInt(input.value.slice(1), 16));
  });
  return input;
}
